import { useForm } from "react-hook-form";
import {
  authControllerSignUp as mutationFn,
  AuthDtoReq,
} from "@/shared/api/generated.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useAutoTranslation,
  useRecordOptionsStore,
  useUserStore,
} from "@/entities";
import { LANG_KEYS, LOCAL_STORAGE_KEYS, QUERY_KEYS } from "@/shared/constants";

type FormData = AuthDtoReq & { confirmPassword: string };

export function useSignUpForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<FormData>();

  const { login } = useUserStore();
  const { t } = useAutoTranslation();
  const queryClient = useQueryClient();
  const { invalidateOptions } = useRecordOptionsStore();

  const formMutation = useMutation({
    mutationFn,
    async onSuccess({ access_token, telegramLink }) {
      login(access_token);
      reset();

      if (telegramLink) {
        window.open(telegramLink, "_blank", "noopener,noreferrer");
      }

      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTES });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FAVORITE });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DELETED });
      await invalidateOptions();
    },
  });

  const NOT_MATCH_PASSWORDS_ERROR = {
    type: "custom",
    message: t(
      LANG_KEYS.PASSWORDS_DONT_MATCH.key,
      LANG_KEYS.PASSWORDS_DONT_MATCH.value,
    ),
  };

  return {
    register,
    handleSubmit: handleSubmit((values) => {
      if (values.password !== values.confirmPassword) {
        setError("password", NOT_MATCH_PASSWORDS_ERROR);
        setError("confirmPassword", NOT_MATCH_PASSWORDS_ERROR);
        return;
      }

      const payload: AuthDtoReq = {
        email: values.email,
        password: values.password,
      };

      const telegramId = localStorage.getItem(LOCAL_STORAGE_KEYS.TELEGRAM_ID);

      if (telegramId) {
        payload.telegramId = telegramId;
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TELEGRAM_ID);
      }

      formMutation.mutate(payload);
    }),
    isLoading: formMutation.isPending,
    errors,
  };
}
